import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuChartColumn, LuChevronDown, LuFileSpreadsheet, LuHistory, LuPackage, LuPlus, LuTable2, LuTags, LuUsers } from 'react-icons/lu';
import PageHero from '../../../components/data/PageHero';
import Button from '../../../components/ui/Button';
import Popover from '../../../components/ui/Popover';
import useStockText from '../stockText';

const MENU_ITEM = 'tw-flex tw-w-full tw-items-start tw-gap-3 tw-rounded-xl tw-bg-transparent tw-px-3 tw-py-2.5 tw-text-left tw-transition-colors hover:tw-bg-slate-900/[0.05] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:hover:tw-bg-white/[0.07]';

/** "Excel" button with the two downloads (all stocks / stock movements). */
const ExcelMenu = ({ onExportStocks, onExportMovements }) => {
  const { label } = useStockText();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const items = [
    { key: 'stocks', icon: LuTable2, title: label('actions.excelAll'), hint: label('actions.excelAllHint'), onClick: onExportStocks },
    { key: 'movements', icon: LuHistory, title: label('actions.excelMovements'), hint: label('actions.excelMovementsHint'), onClick: onExportMovements },
  ];
  return (
    <>
      <Button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={label('actions.excel')}
        className="max-sm:tw-w-10 max-sm:tw-px-0"
      >
        <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />
        <span className="tw-hidden sm:tw-inline">{label('actions.excel')}</span>
        <LuChevronDown aria-hidden="true" className="tw-hidden tw-h-3.5 tw-w-3.5 tw-text-slate-400 sm:tw-block" />
      </Button>
      <Popover anchorRef={buttonRef} open={open} onClose={() => setOpen(false)} align="end" role="menu" label={label('actions.excel')} className="tw-w-72 tw-p-1.5">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={MENU_ITEM}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
            >
              <span className="tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-bg-emerald-500/10 tw-text-emerald-600 dark:tw-text-emerald-300">
                <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
              </span>
              <span className="tw-min-w-0">
                <span className="tw-block tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{item.title}</span>
                <span className="tw-block tw-text-xs tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{item.hint}</span>
              </span>
            </button>
          );
        })}
      </Popover>
    </>
  );
};

ExcelMenu.propTypes = { onExportStocks: PropTypes.func.isRequired, onExportMovements: PropTypes.func.isRequired };

/**
 * Title card of Stok Yönetimi. The stock actions (report, assigned stocks,
 * stock types, Excel, new stock) show only on the Stoklar module; Kargo Takip
 * brings its own.
 */
const StockHeader = ({ showActions, onReport, onAssigned, onTypes, onExportStocks, onExportMovements, onAdd }) => {
  const { label } = useStockText();
  const iconButton = 'max-sm:tw-w-10 max-sm:tw-px-0';
  return (
    <PageHero
      icon={LuPackage}
      gradient="tw-from-amber-400 tw-to-orange-600 tw-shadow-orange-500/30"
      title={label('page.title')}
      subtitle={label('page.subtitle')}
      // Phones: "new stock" across the row, the rest as icon buttons under it.
      // Wider screens: one row when it fits, otherwise the buttons wrap (right-aligned)
      // instead of running out of the card; the title always keeps 22rem.
      actionsClassName="tw-grid tw-grid-cols-[repeat(4,auto)_minmax(0,1fr)] tw-items-center tw-gap-2 sm:tw-flex sm:tw-flex-wrap lg:tw-max-w-[calc(100%-22rem)] lg:tw-justify-end"
    >
      {showActions && (
        <>
          <Button onClick={onReport} aria-label={label('actions.report')} title={label('actions.reportHint')} className={iconButton}>
            <LuChartColumn aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600 dark:tw-text-teal-300" />
            <span className="tw-hidden sm:tw-inline">{label('actions.report')}</span>
          </Button>
          <Button onClick={onAssigned} aria-label={label('actions.assigned')} title={label('actions.assignedHint')} className={iconButton}>
            <LuUsers aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-violet-600 dark:tw-text-violet-300" />
            <span className="tw-hidden sm:tw-inline">{label('actions.assigned')}</span>
          </Button>
          <Button onClick={onTypes} aria-label={label('actions.types')} title={label('actions.typesHint')} className={iconButton}>
            <LuTags aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-amber-600 dark:tw-text-amber-300" />
            <span className="tw-hidden sm:tw-inline">{label('actions.types')}</span>
          </Button>
          <ExcelMenu onExportStocks={onExportStocks} onExportMovements={onExportMovements} />
          <Button variant="primary" onClick={onAdd} className="max-sm:tw-w-full">
            <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" strokeWidth={2.5} />
            {label('actions.add')}
          </Button>
        </>
      )}
    </PageHero>
  );
};

StockHeader.propTypes = {
  showActions: PropTypes.bool,
  onReport: PropTypes.func.isRequired,
  onAssigned: PropTypes.func.isRequired,
  onTypes: PropTypes.func.isRequired,
  onExportStocks: PropTypes.func.isRequired,
  onExportMovements: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

export default StockHeader;
